import type { InputState, ControlMode } from '../types';
import { KeyboardInput } from './KeyboardInput';
import { TouchInput } from './TouchInput';
import { CAM_ORBIT_SPEED } from '../config';
import { isMobile } from '../utils/platform';

export class InputManager {
  private keyboard: KeyboardInput;
  private touch: TouchInput | null = null;
  private mouseDeltaX = 0;
  private mouseDeltaY = 0;
  private pointerLocked = false;
  readonly mobile: boolean;

  mode: ControlMode = 'character';
  private onModeChange: ((mode: ControlMode) => void) | null = null;

  constructor() {
    this.mobile = isMobile();
    this.keyboard = new KeyboardInput();

    if (this.mobile) {
      this.touch = new TouchInput();
    } else {
      document.addEventListener('click', this.requestPointerLock);
      document.addEventListener('pointerlockchange', this.onPointerLockChange);
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('keydown', this.onKeyDown);
    }
  }

  setModeChangeCallback(cb: (mode: ControlMode) => void) {
    this.onModeChange = cb;
  }

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'Tab') {
      e.preventDefault();
      this.toggleMode();
    }
  };

  private toggleMode() {
    if (this.mode === 'character') {
      this.mode = 'camera';
      // Exit pointer lock for free orbit
      if (document.pointerLockElement) {
        document.exitPointerLock();
      }
    } else {
      this.mode = 'character';
    }
    this.onModeChange?.(this.mode);
  }

  private requestPointerLock = () => {
    // Only lock pointer in character mode
    if (this.mode === 'character') {
      document.body.requestPointerLock();
    }
  };

  private onPointerLockChange = () => {
    this.pointerLocked = document.pointerLockElement === document.body;
  };

  private onMouseMove = (e: MouseEvent) => {
    if (!this.pointerLocked) return;
    this.mouseDeltaX += e.movementX * CAM_ORBIT_SPEED;
    this.mouseDeltaY += e.movementY * CAM_ORBIT_SPEED;
  };

  getInput(): InputState {
    if (this.mobile && this.touch) {
      const cameraDelta = this.touch.consumeCameraDelta();
      return {
        moveX: this.touch.getMoveX(),
        moveZ: this.touch.getMoveZ(),
        jump: this.touch.isJump(),
        cameraRotateX: cameraDelta.x,
        cameraRotateY: cameraDelta.y,
        run: false,
      };
    }

    const dx = this.mouseDeltaX;
    const dy = this.mouseDeltaY;
    this.mouseDeltaX = 0;
    this.mouseDeltaY = 0;

    return {
      moveX: this.keyboard.getMoveX(),
      moveZ: this.keyboard.getMoveZ(),
      jump: this.keyboard.isJump(),
      cameraRotateX: dx,
      cameraRotateY: dy,
      run: this.keyboard.isRun(),
    };
  }

  wasEnterPressed(): boolean {
    const kb = this.keyboard.consumeEdgePress('Enter');
    const tap = this.touch?.consumeAction() ?? false;
    return kb || tap;
  }

  /** Drive the on-screen ENTER/EXIT button label and its highlighted state.
   * Desktop is a no-op. Called every frame from the main loop with the
   * current vehicle/near-car state. */
  setTouchActionState(label: string, active: boolean, disabled = false) {
    this.touch?.setActionState(label, active, disabled);
  }

  dispose() {
    this.keyboard.dispose();
    this.touch?.dispose();
    document.removeEventListener('click', this.requestPointerLock);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('keydown', this.onKeyDown);
  }
}
